'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  LayoutDashboard,
  SlidersHorizontal,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  X,
  GitBranch,
  Scale,
  Settings2,
  Sun,
  Moon,
  Bookmark,
  Save,
  Trash2,
} from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useModel } from '@/context/ModelContext';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  badgeVariant?: 'gold' | 'info' | 'dim';
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Executive Dashboard',
    href: '/',
    icon: <LayoutDashboard size={16} />,
    badge: 'LIVE',
    badgeVariant: 'gold',
  },
  {
    label: 'Assumptions & Inputs',
    href: '/assumptions-inputs',
    icon: <SlidersHorizontal size={16} />,
    badge: '30+',
    badgeVariant: 'dim',
  },
  {
    label: 'Financial Projections',
    href: '/financial-projections',
    icon: <BarChart3 size={16} />,
  },
  {
    label: 'Scenario Comparison',
    href: '/scenario-comparison',
    icon: <GitBranch size={16} />,
    badge: 'NEW',
    badgeVariant: 'info',
  },
  {
    label: 'Plant Decision: 2 vs 5 TPH',
    href: '/plant-decision',
    icon: <Scale size={16} />,
    badge: 'NEW',
    badgeVariant: 'info',
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  activeRoute: string;
  isMobileDrawer?: boolean;
  onClose?: () => void;
}

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  activeRoute,
  isMobileDrawer,
  onClose,
}: SidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const { savedScenarios, saveScenario, loadScenario, deleteScenario, activeScenario } = useModel();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const width = collapsed && !isMobileDrawer ? 'w-16' : 'w-64';

  return (
    <aside
      className={`${width} flex flex-col h-full transition-all duration-300 ease-in-out flex-shrink-0`}
      style={{
        minHeight: '100vh',
        background: 'var(--surface-1)',
        borderRight: '1px solid var(--divider)',
      }}
    >
      {/* Logo / Header */}
      <div
        className={`flex items-center h-14 px-4 flex-shrink-0 ${collapsed && !isMobileDrawer ? 'justify-center' : 'justify-between'}`}
        style={{ borderBottom: '1px solid var(--divider)' }}
      >
        {!collapsed || isMobileDrawer ? (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--gold)', color: 'var(--primary-foreground)' }}
              >
                <span className="text-xs font-black tracking-tighter">SG</span>
              </div>
              <div className="min-w-0">
                <span
                  className="font-bold text-xs tracking-widest uppercase block leading-tight"
                  style={{ color: 'var(--text-primary)', letterSpacing: '0.12em' }}
                >
                  ShivaGreen
                </span>
                <span
                  className="text-xs block leading-tight"
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.06em',
                  }}
                >
                  FINANCIAL MODEL · CONFIDENTIAL
                </span>
              </div>
            </div>
            {isMobileDrawer ? (
              <button
                onClick={onClose}
                className="p-1.5 rounded transition-colors flex-shrink-0"
                style={{ color: 'var(--text-tertiary)' }}
                aria-label="Close sidebar"
              >
                <X size={15} />
              </button>
            ) : (
              <button
                onClick={onToggleCollapse}
                className="p-1.5 rounded transition-colors flex-shrink-0"
                style={{ color: 'var(--text-tertiary)' }}
                aria-label="Collapse sidebar"
              >
                <ChevronLeft size={15} />
              </button>
            )}
          </>
        ) : (
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            aria-label="Expand sidebar"
          >
            <ChevronRight size={15} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
        {(!collapsed || isMobileDrawer) && (
          <p
            className="px-3 pb-2 pt-1"
            style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
            }}
          >
            Navigation
          </p>
        )}

        {NAV_ITEMS.map((item) => {
          const isActive = activeRoute === item.href;
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={collapsed && !isMobileDrawer ? item.label : undefined}
              className={`flex items-center gap-2.5 rounded px-2.5 py-2 text-xs font-semibold transition-all duration-150 group relative ${collapsed && !isMobileDrawer ? 'justify-center' : ''}`}
              style={{
                background: isActive ? 'rgba(201,168,76,0.1)' : 'transparent',
                color: isActive ? 'var(--gold)' : 'var(--text-secondary)',
                borderLeft: isActive ? '2px solid var(--gold)' : '2px solid transparent',
                letterSpacing: '0.03em',
              }}
            >
              <span className="flex-shrink-0">{item.icon}</span>
              {(!collapsed || isMobileDrawer) && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge && (
                    <span
                      className="text-xs font-bold px-1.5 py-0.5 rounded-sm"
                      style={{
                        fontSize: '0.55rem',
                        letterSpacing: '0.1em',
                        background:
                          item.badgeVariant === 'gold'
                            ? 'rgba(201,168,76,0.15)'
                            : item.badgeVariant === 'info'
                              ? 'rgba(74,158,255,0.15)'
                              : 'rgba(107,118,148,0.15)',
                        color:
                          item.badgeVariant === 'gold'
                            ? 'var(--gold)'
                            : item.badgeVariant === 'info'
                              ? 'var(--accent)'
                              : 'var(--text-tertiary)',
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {/* Tooltip for collapsed */}
              {collapsed && !isMobileDrawer && (
                <div
                  className="absolute left-full ml-2 px-2.5 py-1.5 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
                  style={{
                    background: 'var(--surface-3)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--divider)',
                    fontSize: '0.7rem',
                  }}
                >
                  {item.label}
                </div>
              )}
            </Link>
          );
        })}



        {/* Project Info */}
        {(!collapsed || isMobileDrawer) && (
          <div className="pt-5">
            <p
              className="px-3 pb-2"
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--text-tertiary)',
              }}
            >
              Project
            </p>
            <div
              className="mx-1 rounded p-3"
              style={{
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.15)',
              }}
            >
              <div className="space-y-1">
                <p
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: 'var(--gold)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  5 TPH Biomass Pellet
                </p>
                <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)' }}>
                  Bihuni, Garhmukteshwar
                </p>
                <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)' }}>
                  Western UP · Aug 2026
                </p>
                <div className="pt-1">
                  <span
                    className="inline-block px-1.5 py-0.5 rounded-sm"
                    style={{
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      background: 'rgba(201,168,76,0.15)',
                      color: 'var(--gold)',
                    }}
                  >
                    CONFIDENTIAL
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hidden Settings / Theme Toggle */}
        {(!collapsed || isMobileDrawer) && (
          <div className="pt-4">
            <button
              onClick={() => setSettingsOpen((prev) => !prev)}
              className="w-full flex items-center gap-2.5 rounded px-2.5 py-2 transition-all duration-150"
              style={{
                color: 'var(--text-tertiary)',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                background: settingsOpen ? 'var(--surface-2)' : 'transparent',
              }}
            >
              <Settings2 size={14} />
              <span className="flex-1 text-left">Display Settings</span>
              <span style={{ fontSize: '0.55rem', opacity: 0.5 }}>{settingsOpen ? '▲' : '▼'}</span>
            </button>
            {settingsOpen && (
              <div
                className="mx-1 mt-1 rounded p-3 space-y-2"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--divider)' }}
              >
                <p
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  Theme
                </p>
                <button
                  onClick={toggleTheme}
                  className="w-full flex items-center justify-between rounded px-2.5 py-2 transition-all"
                  style={{
                    background: 'var(--surface-3)',
                    border: '1px solid var(--divider)',
                    color: 'var(--text-primary)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  <span className="flex items-center gap-2">
                    {theme === 'dark' ? <Moon size={13} /> : <Sun size={13} />}
                    {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </span>
                  <span
                    className="px-1.5 py-0.5 rounded-sm"
                    style={{
                      fontSize: '0.55rem',
                      background: 'rgba(201,168,76,0.15)',
                      color: 'var(--gold)',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {theme === 'dark' ? 'ON' : 'OFF'}
                  </span>
                </button>
                <p style={{ fontSize: '0.58rem', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                  Toggle between executive dark and light document modes.
                </p>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      {(!collapsed || isMobileDrawer) && (
        <div className="px-3 py-3 flex-shrink-0" style={{ borderTop: '1px solid var(--divider)' }}>
          <p style={{ fontSize: '0.58rem', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
            All amber fields are editable inputs. Formulas recalculate instantly.
          </p>
        </div>
      )}
    </aside>
  );
}
